import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Download, Smartphone, X } from 'lucide-react';
import { useLanguage } from '../i18n';

export const PWAInstallButton = ({ variant = 'navbar' }) => {
  const [canInstall, setCanInstall] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    // Check if already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone === true;
    
    if (isStandalone) {
      return; // Already installed
    }

    // Check for iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Listen for install prompt
    const handleInstallPrompt = () => {
      setCanInstall(true);
      // Show banner after 5 seconds for new users
      const dismissed = localStorage.getItem('pwa-banner-dismissed');
      if (!dismissed) {
        setTimeout(() => setShowBanner(true), 5000);
      }
    };

    window.addEventListener('pwaInstallAvailable', handleInstallPrompt);
    
    // Check if prompt is already available
    if (window.deferredPrompt) {
      handleInstallPrompt();
    }

    return () => {
      window.removeEventListener('pwaInstallAvailable', handleInstallPrompt);
    };
  }, []);

  const handleInstall = async () => {
    if (window.installPWA) {
      await window.installPWA();
      setCanInstall(false);
      setShowBanner(false);
    }
  };

  const dismissBanner = () => {
    setShowBanner(false);
    localStorage.setItem('pwa-banner-dismissed', 'true');
  };

  // Navbar button variant
  if (variant === 'navbar') {
    if (!canInstall && !isIOS) return null;

    return (
      <Button
        variant="outline"
        size="sm"
        onClick={isIOS ? undefined : handleInstall}
        className="gap-2 border-orange-500/50 text-orange-600 hover:bg-orange-50"
        title={isIOS ? t('pwa.iosInstructions') : t('pwa.install')}
        data-testid="pwa-install-navbar-btn"
      >
        <Download className="h-4 w-4" />
        <span className="hidden sm:inline">{t('pwa.install')}</span>
      </Button>
    );
  }

  // Banner variant (shows at bottom of screen)
  if (variant === 'banner' && showBanner) {
    return (
      <div 
        className="fixed bottom-3 left-2 right-2 sm:bottom-20 sm:left-auto sm:right-4 sm:max-w-sm z-50 animate-in slide-in-from-bottom-4"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        data-testid="pwa-install-banner"
      >
        <div className="bg-gradient-to-r from-orange-600 to-orange-500 text-white rounded-xl shadow-lg p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-white/20 rounded-lg shrink-0">
              <Smartphone className="h-6 w-6" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-sm">{t('pwa.bannerTitle')}</h4>
              <p className="text-xs text-white/80 mt-1">
                {isIOS ? t('pwa.iosInstructions') : t('pwa.bannerDescription')}
              </p>
              {!isIOS && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={handleInstall}
                  className="mt-3 bg-white text-orange-600 hover:bg-white/90 text-xs h-8"
                  data-testid="pwa-install-banner-btn"
                >
                  <Download className="h-3 w-3 mr-1.5" />
                  {t('pwa.installNow')}
                </Button>
              )}
            </div>
            <button 
              onClick={dismissBanner}
              className="text-white/70 hover:text-white p-1"
              data-testid="pwa-dismiss-banner-btn"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default PWAInstallButton;
